import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { apiResponse } from 'src/app/models/apiResponse.model';
import { OrderByConfig, ApiFilter, FieldsSelection, DefaultFilter } from 'src/app/models/apiParams.model';
import {
    addSubscription, address, bidFilterConfig, company, companyAlias, companyProperty, dealership,
    editSubscription, email, employee, employeeProperty, inspectionFilterConfig, patchRequestPreference, personEmail,
    personPhone, phone, requestPreference, specialism, updatePerson, website
} from 'src/app/models/crm.model';
import { BidFilterUpdate } from 'src/app/models/bidFilter.model';
import { InspectionFilterUpdate } from 'src/app/models/inspectionFilter.model';
import { addPerson, deleteEmployeeAndTransfer, updateCompanyEmail, updatePersonEmail, updateCompanyPhone, updatePersonPhone, updateCompanyAddress, updateCompanyWebsite } from 'src/app/models/request.model';
import { BaseApiService } from '../base/base-api.service';

/**
 * CRM Service - Handles companies, persons, employees, subscriptions, emails, phones, addresses, websites,
 * bid filters, inspection filters, dealerships, specialisms, properties, and related entities
 */
@Injectable({ providedIn: 'root' })
export class CrmService extends BaseApiService {
    private readonly crmPath = '/crm/v1/';

    constructor(http: HttpClient) {
        super(http);
    }

    /*-------------------------
    == COMPANY CALLS ==
    -------------------------*/

    getCompanies(limit: number, page: number, orderBy?: string | OrderByConfig[], filter?: string | ApiFilter, fields?: FieldsSelection, defaultFilter?: DefaultFilter): Observable<apiResponse> {
        return this.getAll<apiResponse>(`${this.crmPath}company`, { limit, page, orderBy, filter, fields, defaultFilter });
    }

    getCompanyId(companyID: number, fields?: string): Observable<apiResponse> {
        return this.getById<apiResponse>(`${this.crmPath}company/`, companyID, { fields });
    }

    addCompany(data: company): Observable<apiResponse> {
        return this.post<apiResponse>(`${this.crmPath}company`, data, 'json');
    }

    updateCompany(id: number, data: company): Observable<apiResponse> {
        return this.put<apiResponse>(`${this.crmPath}company/`, id, data, 'json');
    }

    getCompanyTypes(limit: number, page: number, orderBy?: string | OrderByConfig[], filter?: string | ApiFilter, fields?: FieldsSelection): Observable<apiResponse> {
        return this.getAll<apiResponse>(`${this.crmPath}company-type/`, { limit, page, orderBy, filter, fields });
    }

    getExpertBids(companyId: number, limit: number, page: number, orderBy?: string | OrderByConfig[], filter?: string | ApiFilter, fields?: FieldsSelection, defaultFilter?: DefaultFilter): Observable<apiResponse> {
        return this.getAll<apiResponse>(`${this.crmPath}company/${companyId}/expert-bids`, { limit, page, orderBy, filter, fields, defaultFilter });
    }

    /*-------------------------
    == PERSON CALLS ==
    -------------------------*/

    getPersons(limit: number, page: number, orderBy?: string, filter?: string | ApiFilter, fields?: FieldsSelection, defaultFilter?: DefaultFilter): Observable<apiResponse> {
        return this.getAll<apiResponse>(`${this.crmPath}person`, { limit, page, orderBy, filter, fields, defaultFilter });
    }

    getPersonID(personID: number): Observable<apiResponse> {
        return this.getById<apiResponse>(`${this.crmPath}person/`, personID);
    }

    addPerson(data: addPerson): Observable<apiResponse> {
        return this.post<apiResponse>(`${this.crmPath}person/`, data, 'json');
    }

    updatePerson(id: number, data: updatePerson): Observable<apiResponse> {
        return this.put<apiResponse>(`${this.crmPath}person/`, id, data, 'json');
    }

    /*-------------------------
    == EMPLOYEE CALLS ==
    -------------------------*/

    getEmployees(limit: number, page: number, orderBy?: string | null, filter?: string | ApiFilter | null, fields?: FieldsSelection | null, defaultFilter?: DefaultFilter | null): Observable<apiResponse> {
        return this.getAll<apiResponse>(`${this.crmPath}employee/`, { limit, page, orderBy: orderBy ?? undefined, filter: filter ?? undefined, fields: fields ?? undefined, defaultFilter: defaultFilter ?? undefined });
    }

    getEmployeeID(employeeID: number): Observable<apiResponse> {
        return this.getById<apiResponse>(`${this.crmPath}employee/`, employeeID);
    }

    addEmployee(data: employee): Observable<apiResponse> {
        return this.post<apiResponse>(`${this.crmPath}employee`, data, 'json');
    }

    updateEmployee(id: number, data: employee): Observable<apiResponse> {
        return this.put<apiResponse>(`${this.crmPath}employee/`, id, data, 'json');
    }

    deleteEmployee(id: number): Observable<apiResponse> {
        return this.delete<apiResponse>(`${this.crmPath}employee/`, id);
    }

    deleteEmployeeAndTransfer(id: number, data: deleteEmployeeAndTransfer): Observable<apiResponse> {
        return this.putPath<apiResponse>(`${this.crmPath}employee/${id}/remove`, data, 'json');
    }

    sendCompanyLogin(employeeId: number): Observable<apiResponse> {
        return this.getAll<apiResponse>(`${this.crmPath}employee/${employeeId}/company-login`);
    }

    getEmployeePropertyId(id: number, fields?: FieldsSelection): Observable<apiResponse> {
        return this.getById<apiResponse>(`${this.crmPath}employee-property/`, id, { fields });
    }

    updateEmployeeProperty(id: number, data: employeeProperty): Observable<apiResponse> {
        return this.put<apiResponse>(`${this.crmPath}employee-property/`, id, data, 'json');
    }

    deleteEmployeeProperty(id: number): Observable<apiResponse> {
        return this.delete<apiResponse>(`${this.crmPath}employee-property/`, id);
    }

    /*-------------------------
    == SUBSCRIPTION CALLS ==
    -------------------------*/

    getSubscriptionTypes(limit: number, page: number, orderBy?: string, filter?: string | ApiFilter, fields?: FieldsSelection, defaultFilter?: DefaultFilter): Observable<apiResponse> {
        return this.getAll<apiResponse>(`${this.crmPath}subscription-type/`, { limit, page, orderBy, filter, fields, defaultFilter });
    }

    getSubscription(fields: string, id: number): Observable<apiResponse> {
        return this.getById<apiResponse>(`${this.crmPath}subscription/`, id, { fields });
    }

    addSubscription(data: addSubscription): Observable<apiResponse> {
        return this.post<apiResponse>(`${this.crmPath}subscription`, data, 'json');
    }

    updateSubscription(id: number, data: editSubscription): Observable<apiResponse> {
        return this.put<apiResponse>(`${this.crmPath}subscription/`, id, data, 'json');
    }

    deleteSubscription(id: number): Observable<apiResponse> {
        return this.delete<apiResponse>(`${this.crmPath}subscription/`, id);
    }

    /*-------------------------
    == EMAIL CALLS ==
    -------------------------*/

    getEmailTypes(limit: number, page: number, orderBy?: string | OrderByConfig[], filter?: string | ApiFilter, fields?: FieldsSelection): Observable<apiResponse> {
        return this.getAll<apiResponse>(`${this.crmPath}email-type`, { limit, page, orderBy, filter, fields });
    }

    getCompanyEmail(limit: number, page: number, orderBy?: string | OrderByConfig[], filter?: string | ApiFilter, fields?: FieldsSelection, defaultFilter?: DefaultFilter): Observable<apiResponse> {
        return this.getAll<apiResponse>(`${this.crmPath}company-email/`, { limit, page, orderBy, filter, fields, defaultFilter });
    }

    addEmail(data: email): Observable<apiResponse> {
        return this.post<apiResponse>(`${this.crmPath}company-email/`, data, 'json');
    }

    updateCompanyEmail(id: number, data: updateCompanyEmail): Observable<apiResponse> {
        return this.put<apiResponse>(`${this.crmPath}company-email/`, id, data, 'json');
    }

    deleteCompanyEmail(id: number): Observable<apiResponse> {
        return this.delete<apiResponse>(`${this.crmPath}company-email/`, id);
    }

    getPersonEmail(limit: number, page: number, orderBy?: string | OrderByConfig[], filter?: string | ApiFilter, fields?: FieldsSelection, defaultFilter?: DefaultFilter): Observable<apiResponse> {
        return this.getAll<apiResponse>(`${this.crmPath}person-email/`, { limit, page, orderBy, filter, fields, defaultFilter });
    }

    addPersonEmail(data: personEmail): Observable<apiResponse> {
        return this.post<apiResponse>(`${this.crmPath}person-email/`, data, 'json');
    }

    updatePersonEmail(id: number, data: updatePersonEmail): Observable<apiResponse> {
        return this.put<apiResponse>(`${this.crmPath}person-email/`, id, data, 'json');
    }

    deletePersonEmail(id: number): Observable<apiResponse> {
        return this.delete<apiResponse>(`${this.crmPath}person-email/`, id);
    }

    /*-------------------------
    == PHONE CALLS ==
    -------------------------*/

    getPhoneTypes(limit: number, page: number, orderBy?: string | OrderByConfig[], filter?: string | ApiFilter, fields?: FieldsSelection): Observable<apiResponse> {
        return this.getAll<apiResponse>(`${this.crmPath}phone-type/`, { limit, page, orderBy, filter, fields });
    }

    addPhone(data: phone): Observable<apiResponse> {
        return this.post<apiResponse>(`${this.crmPath}company-phone/`, data, 'json');
    }

    updateCompanyPhone(id: number, data: updateCompanyPhone): Observable<apiResponse> {
        return this.put<apiResponse>(`${this.crmPath}company-phone/`, id, data, 'json');
    }

    deleteCompanyPhone(id: number): Observable<apiResponse> {
        return this.delete<apiResponse>(`${this.crmPath}company-phone/`, id);
    }

    addPersonPhone(data: personPhone): Observable<apiResponse> {
        return this.post<apiResponse>(`${this.crmPath}person-phone/`, data, 'json');
    }

    updatePersonPhone(id: number, data: updatePersonPhone): Observable<apiResponse> {
        return this.put<apiResponse>(`${this.crmPath}person-phone/`, id, data, 'json');
    }

    deletePersonPhone(id: number): Observable<apiResponse> {
        return this.delete<apiResponse>(`${this.crmPath}person-phone/`, id);
    }

    /*-------------------------
    == ADDRESS CALLS ==
    -------------------------*/

    getAddressTypes(limit: number, page: number, orderBy?: string | OrderByConfig[], filter?: string | ApiFilter, fields?: FieldsSelection): Observable<apiResponse> {
        return this.getAll<apiResponse>(`${this.crmPath}address-type/`, { limit, page, orderBy, filter, fields });
    }

    addAddress(data: address): Observable<apiResponse> {
        return this.post<apiResponse>(`${this.crmPath}company-address/`, data, 'json');
    }

    updateCompanyAddress(id: number, data: updateCompanyAddress): Observable<apiResponse> {
        return this.put<apiResponse>(`${this.crmPath}company-address/`, id, data, 'json');
    }

    deleteCompanyAddress(id: number): Observable<apiResponse> {
        return this.delete<apiResponse>(`${this.crmPath}company-address/`, id);
    }

    /*-------------------------
    == WEBSITE CALLS ==
    -------------------------*/

    getWebsiteTypes(limit: number, page: number, orderBy?: string | OrderByConfig[], filter?: string | ApiFilter, fields?: FieldsSelection): Observable<apiResponse> {
        return this.getAll<apiResponse>(`${this.crmPath}website-type`, { limit, page, orderBy, filter, fields });
    }

    addWebsite(data: website): Observable<apiResponse> {
        return this.post<apiResponse>(`${this.crmPath}company-website/`, data, 'json');
    }

    updateCompanyWebsite(id: number, data: updateCompanyWebsite): Observable<apiResponse> {
        return this.put<apiResponse>(`${this.crmPath}company-website/`, id, data, 'json');
    }

    deleteCompanyWebsite(id: number): Observable<apiResponse> {
        return this.delete<apiResponse>(`${this.crmPath}company-website/`, id);
    }

    /*-------------------------
    == BID FILTER CALLS ==
    -------------------------*/

    getBidFilterConfig(id: number, fields?: FieldsSelection): Observable<apiResponse> {
        return this.getById<apiResponse>(`${this.crmPath}bid-filter-config/`, id, { fields });
    }

    addBidFilterConfiguration(data: bidFilterConfig): Observable<apiResponse> {
        return this.post<apiResponse>(`${this.crmPath}bid-filter-config/`, data, 'json');
    }

    updateBidFilterConfiguration(id: number, data: bidFilterConfig): Observable<apiResponse> {
        return this.put<apiResponse>(`${this.crmPath}bid-filter-config/`, id, data, 'json');
    }

    deleteBidFilterConfig(id: number): Observable<apiResponse> {
        return this.delete<apiResponse>(`${this.crmPath}bid-filter-config/`, id);
    }

    getBidFilter(id: number, fields?: FieldsSelection): Observable<apiResponse> {
        return this.getById<apiResponse>(`${this.crmPath}bid-filter/`, id, { fields });
    }

    addBidFilter(data: BidFilterUpdate): Observable<apiResponse> {
        return this.post<apiResponse>(`${this.crmPath}bid-filter/`, data, 'json');
    }

    updateBidFilter(id: number, data: BidFilterUpdate): Observable<apiResponse> {
        return this.put<apiResponse>(`${this.crmPath}bid-filter/`, id, data, 'json');
    }

    deleteBidFilter(id: number): Observable<apiResponse> {
        return this.delete<apiResponse>(`${this.crmPath}bid-filter/`, id);
    }

    getBidFilterOperators(): Observable<apiResponse> {
        return this.getAll<apiResponse>(`${this.crmPath}bid-filter/operator/`);
    }

    getBidFilterFields(): Observable<apiResponse> {
        return this.getAll<apiResponse>(`${this.crmPath}bid-filter/field/`);
    }

    testBidFilter(id: number, fields?: FieldsSelection): Observable<apiResponse> {
        return this.getById<apiResponse>(`${this.crmPath}bid-filter-config/`, `${id}/test`, { fields });
    }

    testSpecificBidFilter(id: number, fields?: FieldsSelection): Observable<apiResponse> {
        return this.getById<apiResponse>(`${this.crmPath}bid-filter/`, `${id}/test`, { fields });
    }

    /*-------------------------
    == INSPECTION FILTER CALLS ==
    -------------------------*/

    getInspectionFilterConfig(id: number): Observable<apiResponse> {
        return this.getById<apiResponse>(`${this.crmPath}inspection-filter-config/`, id);
    }

    addInspectionFilterConfiguration(data: inspectionFilterConfig): Observable<apiResponse> {
        return this.post<apiResponse>(`${this.crmPath}inspection-filter-config/`, data, 'json');
    }

    updateInspectionFilterConfig(id: number, data: inspectionFilterConfig): Observable<apiResponse> {
        return this.put<apiResponse>(`${this.crmPath}inspection-filter-config/`, id, data, 'json');
    }

    deleteInspectionFilterConfig(id: number): Observable<apiResponse> {
        return this.delete<apiResponse>(`${this.crmPath}inspection-filter-config/`, id);
    }

    getInspectionFilter(id: number): Observable<apiResponse> {
        return this.getById<apiResponse>(`${this.crmPath}inspection-filter/`, id);
    }

    addInspectionFilter(data: InspectionFilterUpdate): Observable<apiResponse> {
        return this.post<apiResponse>(`${this.crmPath}inspection-filter/`, data, 'json');
    }

    updateInspectionFilter(id: number, data: InspectionFilterUpdate): Observable<apiResponse> {
        return this.put<apiResponse>(`${this.crmPath}inspection-filter/`, id, data, 'json');
    }

    deleteInspectionFilter(id: number): Observable<apiResponse> {
        return this.delete<apiResponse>(`${this.crmPath}inspection-filter/`, id);
    }

    getInspectionFilterOperators(): Observable<apiResponse> {
        return this.getAll<apiResponse>(`${this.crmPath}inspection-filter/operator/`);
    }

    getInspectionFilterFields(): Observable<apiResponse> {
        return this.getAll<apiResponse>(`${this.crmPath}inspection-filter/field/`);
    }

    verifyInspectionFilterForValuation(valuationId: number): Observable<apiResponse> {
        return this.getAll<apiResponse>(`${this.crmPath}inspection-filter-config/verify`, {
            customParams: { valuationId: String(valuationId) }
        });
    }

    /*-------------------------
    == DEALERSHIP CALLS ==
    -------------------------*/

    getDealership(id: number, fields?: FieldsSelection): Observable<apiResponse> {
        return this.getById<apiResponse>(`${this.crmPath}dealership/`, id, { fields });
    }

    addDealership(data: dealership): Observable<apiResponse> {
        return this.post<apiResponse>(`${this.crmPath}dealership/`, data, 'json');
    }

    updateDealership(id: number, data: dealership): Observable<apiResponse> {
        return this.put<apiResponse>(`${this.crmPath}dealership/`, id, data, 'json');
    }

    deleteDealership(id: number): Observable<apiResponse> {
        return this.delete<apiResponse>(`${this.crmPath}dealership/`, id);
    }

    /*-------------------------
    == SPECIALISM CALLS ==
    -------------------------*/

    getSpecialism(id: number, fields?: FieldsSelection): Observable<apiResponse> {
        return this.getById<apiResponse>(`${this.crmPath}specialism/`, id, { fields });
    }

    addSpecialism(data: specialism): Observable<apiResponse> {
        return this.post<apiResponse>(`${this.crmPath}specialism/`, data, 'json');
    }

    updateSpecialism(id: number, data: specialism): Observable<apiResponse> {
        return this.put<apiResponse>(`${this.crmPath}specialism/`, id, data, 'json');
    }

    deleteSpecialism(id: number): Observable<apiResponse> {
        return this.delete<apiResponse>(`${this.crmPath}specialism/`, id);
    }

    /*-------------------------
    == PROPERTY CALLS ==
    -------------------------*/

    getPropertyTypes(limit: number, page: number, orderBy?: string | OrderByConfig[], filter?: string | ApiFilter, fields?: FieldsSelection): Observable<apiResponse> {
        return this.getAll<apiResponse>(`${this.crmPath}property-type/`, { limit, page, orderBy, filter, fields });
    }

    getPropertyTypeId(id: number, fields?: FieldsSelection): Observable<apiResponse> {
        return this.getById<apiResponse>(`${this.crmPath}property-type/`, id, { fields });
    }

    getCompanyProperty(limit: number, page: number, fields?: string | null, defaultFilter?: string | null, filter?: string | null, groupBy?: string | null, orderBy?: string | null): Observable<apiResponse> {
        return this.getAll<apiResponse>(`${this.crmPath}company-property/`, { limit, page, orderBy: orderBy ?? undefined, filter: filter ?? undefined, fields: fields ?? undefined, defaultFilter: defaultFilter ?? undefined, groupBy: groupBy ?? undefined });
    }

    addCompanyProperty(data: companyProperty): Observable<apiResponse> {
        return this.post<apiResponse>(`${this.crmPath}company-property/`, data, 'json');
    }

    updateCompanyProperty(id: number, data: companyProperty): Observable<apiResponse> {
        return this.put<apiResponse>(`${this.crmPath}company-property/`, id, data, 'json');
    }

    deleteCompanyProperty(id: number): Observable<apiResponse> {
        return this.delete<apiResponse>(`${this.crmPath}company-property/`, id);
    }

    getPersonPropertyId(id: number, fields?: FieldsSelection): Observable<apiResponse> {
        return this.getById<apiResponse>(`${this.crmPath}person-property/`, id, { fields });
    }

    /*-------------------------
    == ALIAS CALLS ==
    -------------------------*/

    getCompanyAlias(id: number, fields?: FieldsSelection): Observable<apiResponse> {
        return this.getById<apiResponse>(`${this.crmPath}company-alias/`, id, { fields });
    }

    addCompanyAlias(data: companyAlias): Observable<apiResponse> {
        return this.post<apiResponse>(`${this.crmPath}company-alias/`, data, 'json');
    }

    updateCompanyAlias(id: number, data: companyAlias): Observable<apiResponse> {
        return this.put<apiResponse>(`${this.crmPath}company-alias/`, id, data, 'json');
    }

    deleteCompanyAlias(id: number): Observable<apiResponse> {
        return this.delete<apiResponse>(`${this.crmPath}company-alias/`, id);
    }

    /*-------------------------
    == REQUEST PREFERENCE CALLS ==
    -------------------------*/

    addRequestPreference(data: requestPreference): Observable<apiResponse> {
        return this.post<apiResponse>(`${this.crmPath}request-preference`, data, 'json');
    }

    updateRequestPreference(id: number, data: requestPreference): Observable<apiResponse> {
        return this.put<apiResponse>(`${this.crmPath}request-preference/`, id, data, 'json');
    }

    patchRequestPreference(id: number, data: patchRequestPreference): Observable<apiResponse> {
        return this.http.patch<apiResponse>(`${this.api_base}${this.crmPath}request-preference/${id}`, data, {
            headers: this.getHeaders('form-urlencoded')
        });
    }

    deleteRequestPreference(id: number): Observable<apiResponse> {
        return this.delete<apiResponse>(`${this.crmPath}request-preference/`, id);
    }

    /*-------------------------
    == FUNCTION TYPES CALLS ==
    -------------------------*/

    getFunctionTypes(): Observable<apiResponse> {
        return this.getAll<apiResponse>(`${this.crmPath}function-type/`);
    }
}
